import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const userTestimonials = [
  {
    id: 1,
    name: 'Sarah M.',
    age: 34,
    role: 'Chronic Migraine sufferer',
    initials: 'SM',
    avatarColor: 'bg-purple-100 text-purple-700',
    stars: 5,
    before: 'Could never explain headache patterns clearly to her neurologist.',
    after: 'Neurologist immediately adjusted medication based on the 2-week tracking report.',
    quote: 'Before MyBodyToday I could never explain my headache patterns clearly. After 2 weeks of tracking, my neurologist immediately adjusted my medication based on the report I showed her.',
  },
  {
    id: 2,
    name: 'James T.',
    age: 58,
    role: 'Managing Type 2 Diabetes',
    initials: 'JT',
    avatarColor: 'bg-blue-100 text-blue-700',
    stars: 5,
    before: 'Forgot half his symptoms by the time he got to his GP.',
    after: 'Now walks in with a full 30-day report his doctor calls the most useful thing a patient has ever brought.',
    quote: 'I used to forget half my symptoms by the time I got to my GP. Now I walk in with a full 30-day report. My doctor said it\'s the most useful thing a patient has ever brought to an appointment.',
  },
  {
    id: 3,
    name: 'Priya K.',
    age: 41,
    role: 'Caregiver for elderly mother',
    initials: 'PK',
    avatarColor: 'bg-rose-100 text-rose-700',
    stars: 5,
    before: 'Doctors had missed a recurring symptom pattern in her mother for months.',
    after: 'Daily tracking revealed the pattern — the consultation report changed everything.',
    quote: 'Tracking my mum\'s symptoms daily meant we caught a pattern the doctors had missed for months. The consultation report changed everything.',
  },
];

const doctorEndorsements = [
  {
    id: 4,
    name: 'Dr. Rebecca Walsh',
    role: 'GP',
    initials: 'RW',
    avatarColor: 'bg-teal-100 text-teal-700',
    stars: 5,
    quote: 'Patient-generated symptom summaries like those from MyBodyToday genuinely improve consultation quality. Patients communicate more clearly and we can make better-informed decisions.',
  },
  {
    id: 5,
    name: 'Dr. Michael Osei',
    role: 'Neurologist',
    initials: 'MO',
    avatarColor: 'bg-indigo-100 text-indigo-700',
    stars: 5,
    quote: 'I recommend symptom tracking apps to all my chronic condition patients. Having a structured timeline removes the guesswork from consultations.',
  },
];

const StarRating = ({ count = 5 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: count })?.map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const UserStoryCard = ({ testimonial, index }) => (
  <motion.div
    initial={{ y: 30, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 0.1 * index }}
    className="bg-white rounded-2xl border border-border shadow-therapeutic hover:shadow-therapeutic-lg transition-all duration-300 hover:-translate-y-1 p-6 flex flex-col h-full">
    
    {/* Before / After badges */}
    <div className="flex gap-2 mb-4">
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-100 rounded-full text-xs font-semibold text-red-600">
        Before
      </span>
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-100 rounded-full text-xs font-semibold text-green-600">
        After
      </span>
    </div>

    {/* Before / After scenario */}
    <div className="space-y-2 mb-5">
      <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
        <span className="text-red-500 font-bold text-xs mt-0.5 flex-shrink-0">✗</span>
        <p className="text-xs text-red-700 leading-relaxed">{testimonial?.before}</p>
      </div>
      <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
        <span className="text-green-600 font-bold text-xs mt-0.5 flex-shrink-0">✓</span>
        <p className="text-xs text-green-700 leading-relaxed">{testimonial?.after}</p>
      </div>
    </div>

    {/* Quote */}
    <div className="relative flex-1 mb-5">
      <Quote className="w-6 h-6 text-primary/20 absolute -top-1 -left-1" />
      <p className="text-sm text-muted-foreground leading-relaxed pl-4 italic">
        &ldquo;{testimonial?.quote}&rdquo;
      </p>
    </div>

    {/* Author */}
    <div className="flex items-center gap-3 pt-4 border-t border-border">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${testimonial?.avatarColor}`}>
        {testimonial?.initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">{testimonial?.name}, {testimonial?.age}</p>
        <p className="text-xs text-muted-foreground truncate">{testimonial?.role}</p>
      </div>
      <StarRating count={testimonial?.stars} />
    </div>
  </motion.div>
);

const DoctorEndorsementCard = ({ endorsement, index }) => (
  <motion.div
    initial={{ y: 30, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 0.15 * index }}
    className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl border border-primary/20 shadow-therapeutic hover:shadow-therapeutic-lg transition-all duration-300 hover:-translate-y-1 p-6 flex flex-col h-full">
    
    {/* Clinician badge */}
    <div className="mb-4">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 rounded-full text-xs font-semibold text-primary">
        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
        Clinician Endorsement
      </span>
    </div>

    {/* Quote */}
    <div className="relative flex-1 mb-5">
      <Quote className="w-6 h-6 text-primary/20 absolute -top-1 -left-1" />
      <p className="text-sm text-foreground leading-relaxed pl-4 font-medium">
        &ldquo;{endorsement?.quote}&rdquo;
      </p>
    </div>

    {/* Author */}
    <div className="flex items-center gap-3 pt-4 border-t border-primary/10">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${endorsement?.avatarColor}`}>
        {endorsement?.initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">{endorsement?.name}</p>
        <p className="text-xs text-muted-foreground">{endorsement?.role}</p>
      </div>
      <StarRating count={endorsement?.stars} />
    </div>
  </motion.div>
);

const TestimonialsSection = ({ embedded = false }) => {
  const content = (
    <div className="w-full max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
      
      {!embedded && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-10">
          
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4">
            <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
            <span className="text-sm font-semibold text-amber-700">Real stories, real results</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-heading font-bold text-foreground mb-3">
            Trusted by patients and clinicians
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            See how MyBodyToday helps people communicate more clearly with their doctors.
          </p>
        </motion.div>
      )}

      {/* User stories */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5 text-center">Patient Stories</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {userTestimonials?.map((t, i) => (
            <UserStoryCard key={t?.id} testimonial={t} index={i} />
          ))}
        </div>
      </div>

      {/* Doctor endorsements */}
      <div className="mt-8">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5 text-center">Clinician Endorsements</p>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {doctorEndorsements?.map((d, i) => (
            <DoctorEndorsementCard key={d?.id} endorsement={d} index={i} />
          ))}
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return <div className="py-8 sm:py-10">{content}</div>;
  }

  return (
    <section id="testimonials" className="py-10 sm:py-16 bg-gradient-to-br from-card via-muted to-background">
      {content}
    </section>
  );
};

export default TestimonialsSection;
